import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Avatar,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  useMediaQuery,
  Fab,
  Zoom
} from '@mui/material';
import {
  Storefront as StorefrontIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Replay as ReplayIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp,
  onSnapshot,
  getDocs,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

interface StoreAction {
  id: string;
  actionType: 'Pickup' | 'Return' | 'Assist';
  name: string;
  Qrid: string;
  timestamp: Timestamp;
  who_collect?: string;
  actionstatus?: 'Pending' | 'In Progress' | 'Completed';
  Crew?: string;
  inProgressStartTime?: Timestamp;
  timestamps_completed?: Timestamp;
}

interface StoreActionsProps {
  onActionUpdated?: () => void;
}

const StoreActions: React.FC<StoreActionsProps> = ({ onActionUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [actions, setActions] = useState<StoreAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'today' | 'history'>('today');
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0
  });
  
  // Add a ticker to force UI updates for elapsed time
  const [timeTicker, setTimeTicker] = useState(0);
  
  // For history search
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchDate, setSearchDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [searchQrid, setSearchQrid] = useState('');
  const [searchName, setSearchName] = useState('');
  const [historyResults, setHistoryResults] = useState<StoreAction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Keep track of listeners to clean them up
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<number | null>(null);

  // Format date for more readable display
  const formatDate = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate time elapsed since action was initiated
  const calculateElapsedTime = (timestamp: Timestamp): string => {
    // Force re-calculation of elapsed time on each render
    // This is a no-op that makes ESLint happy by using timeTicker
    // It ensures this function re-runs when timeTicker changes
    const _ = timeTicker;
    
    const now = Date.now();
    const actionTime = timestamp.toMillis();
    const elapsed = now - actionTime;
    
    const minutes = Math.floor(elapsed / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ago`;
    } else {
      return `${minutes}m ago`;
    }
  };

  // Helper functions for date manipulation without date-fns
  const getStartOfDay = (dateString: string): Date => {
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getEndOfDay = (dateString: string): Date => {
    const date = new Date(dateString);
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const isToday = (dateString: string): boolean => {
    const today = new Date();
    const date = new Date(dateString);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Set up real-time listener for today's actions
  const setupRealTimeUpdates = () => {
    try {
      // Clean up previous listener if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      setLoading(true);
      setError(null);
      
      // Get today's start and end timestamps
      const today = new Date();
      // Create separate Date objects for start and end
      const startOfToday = new Date(today);
      startOfToday.setHours(0, 0, 0, 0);
      
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);
      
      // Create a query for today's actions
      const todayQuery = query(
        collection(db, 'StoreActions'),
        where('timestamp', '>=', Timestamp.fromDate(startOfToday)),
        where('timestamp', '<=', Timestamp.fromDate(endOfToday)),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        todayQuery,
        (snapshot) => {
          const fetchedActions: StoreAction[] = [];
          let pendingCount = 0;
          let inProgressCount = 0;
          let completedCount = 0;
          
          snapshot.forEach((doc) => {
            const data = doc.data() as Record<string, any>;
            const action = { id: doc.id, ...data } as StoreAction;
            fetchedActions.push(action);
            
            // Count by status
            if (!action.actionstatus || action.actionstatus === 'Pending') {
              pendingCount++;
            } else if (action.actionstatus === 'In Progress') {
              inProgressCount++;
            } else if (action.actionstatus === 'Completed') {
              completedCount++;
            }
          });
          
          // Update state
          setActions(fetchedActions);
          setStatusCounts({
            pending: pendingCount,
            inProgress: inProgressCount,
            completed: completedCount
          });
          setLoading(false);
          
          // Trigger the callback if provided
          if (onActionUpdated) {
            onActionUpdated();
          }
        },
        (err) => {
          console.error('Error in real-time listener:', err);
          setError('Failed to set up real-time updates. Please try refreshing the page.');
          setLoading(false);
        }
      );
      
      // Store the unsubscribe function
      unsubscribeRef.current = unsubscribe;
      
    } catch (err) {
      console.error('Error setting up real-time updates:', err);
      setError('Failed to set up real-time updates. Please try refreshing the page.');
      setLoading(false);
    }
  };
  
  // Search historical actions
  const searchHistoricalActions = async () => {
    if (!searchDate && !searchQrid && !searchName) {
      return; // Don't search if no criteria provided
    }
    
    setHistoryLoading(true);
    try {
      let historicalQuery: any = collection(db, 'StoreActions');
      
      // Build the query based on provided search criteria
      const queryFilters = [];
      
      // If date is provided, add date filters
      if (searchDate) {
        const startOfSearchDate = getStartOfDay(searchDate);
        const endOfSearchDate = getEndOfDay(searchDate);
        
        // Only add date filter if not searching for today (since we already have today's data)
        if (!isToday(searchDate)) {
          queryFilters.push(
            where('timestamp', '>=', Timestamp.fromDate(startOfSearchDate)),
            where('timestamp', '<=', Timestamp.fromDate(endOfSearchDate))
          );
        }
      }
      
      // Add QR ID filter if provided
      if (searchQrid) {
        queryFilters.push(where('Qrid', '==', searchQrid.trim()));
      }
      
      // Add name filter if provided
      if (searchName) {
        // For simplicity, we're using a direct equals match
        // In a more advanced implementation, you might want to use startsWith or a more flexible search
        queryFilters.push(where('name', '==', searchName.trim()));
      }
      
      // Construct the query with all filters
      historicalQuery = query(
        historicalQuery,
        ...queryFilters,
        orderBy('timestamp', 'desc'),
        limit(100) // Limit to 100 historical records
      );
      
      // Execute the query
      const snapshot = await getDocs(historicalQuery);
      const results: StoreAction[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data() as Record<string, any>;
        results.push({ id: doc.id, ...data } as StoreAction);
      });
      
      setHistoryResults(results);
    } catch (err) {
      console.error('Error searching historical actions:', err);
      setError('Failed to search historical actions. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Set up data when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 'today') {
      setupRealTimeUpdates();
      
      // Set up interval to update time display every 30 seconds
      if (timerRef.current === null) {
        timerRef.current = window.setInterval(() => {
          setTimeTicker(prev => prev + 1);
        }, 30000); // Update every 30 seconds
      }
    }
    
    // Cleanup listener and timer when component unmounts or tab changes
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeTab]);
  
  // Additional effect to ensure we clean up the timer when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'today' | 'history') => {
    setActiveTab(newValue);
  };

  // Open history search dialog
  const handleOpenHistorySearch = () => {
    setHistoryOpen(true);
  };

  // Close history search dialog
  const handleCloseHistorySearch = () => {
    setHistoryOpen(false);
  };

  // Execute history search
  const handleSearchHistory = () => {
    searchHistoricalActions();
    setHistoryOpen(false);
  };

  // Format date for display
  const formatDisplayDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Get status color
  const getStatusColor = (status?: string) => {
    if (!status || status === 'Pending') return theme.palette.warning.main;
    if (status === 'In Progress') return theme.palette.info.main;
    if (status === 'Completed') return theme.palette.success.main;
    return theme.palette.grey[500];
  };

  // Get status icon component
  const getStatusIcon = (status?: string) => {
    if (!status || status === 'Pending') return <StorefrontIcon />;
    if (status === 'In Progress') return <AccessTimeIcon />;
    if (status === 'Completed') return <CheckCircleIcon />;
    return <StorefrontIcon />;
  };

  // Get action type color
  const getActionTypeColor = (type: 'Pickup' | 'Return' | 'Assist') => {
    if (type === 'Pickup') return theme.palette.primary.main;
    if (type === 'Return') return theme.palette.secondary.main;
    if (type === 'Assist') return theme.palette.info.main; // Color for Assist type
    return theme.palette.grey[500]; // Fallback
  };

  // Render status cards
  const renderStatusCards = () => {
    return (
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        {isMobile ? (
          // Mobile view - stacked cards with simpler, more compact design
          <>
            {/* Pending Card - Mobile */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 1.5,
                backgroundColor: '#FEF8F1',
                borderColor: '#FEA41D',
                borderRadius: '8px',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorefrontIcon sx={{ color: '#F5820D', mr: 1, fontSize: '1.1rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#333' }}>
                      Pending
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                    {statusCounts.pending}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Actions waiting to be processed
                </Typography>
              </CardContent>
            </Card>
            
            {/* In Progress Card - Mobile */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 1.5,
                backgroundColor: '#EBF5FB',
                borderColor: '#3498DB',
                borderRadius: '8px',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ color: '#1976D2', mr: 1, fontSize: '1.1rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#333' }}>
                      In Progress
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                    {statusCounts.inProgress}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Actions currently being handled
                </Typography>
              </CardContent>
            </Card>
            
            {/* Completed Card - Mobile */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 1.5,
                backgroundColor: '#E8F5E9',
                borderColor: '#4CAF50',
                borderRadius: '8px',
                overflow: 'visible'
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ color: '#2E7D32', mr: 1, fontSize: '1.1rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#333' }}>
                      Completed
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>
                    {statusCounts.completed}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Actions successfully completed
                </Typography>
              </CardContent>
            </Card>
          </>
        ) : (
          // Desktop view - use existing card grid layout
          <Grid container spacing={3}>
            {/* Pending Card - Desktop */}
            <Grid item xs={12} sm={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  backgroundColor: `${theme.palette.warning.light}20`,
                  borderColor: theme.palette.warning.main,
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StorefrontIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">Pending</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {statusCounts.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actions waiting to be processed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* In Progress Card - Desktop */}
            <Grid item xs={12} sm={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  backgroundColor: `${theme.palette.info.light}20`,
                  borderColor: theme.palette.info.main,
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">In Progress</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {statusCounts.inProgress}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actions currently being handled
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Completed Card - Desktop */}
            <Grid item xs={12} sm={4}>
              <Card 
                variant="outlined" 
                sx={{ 
                  backgroundColor: `${theme.palette.success.light}20`,
                  borderColor: theme.palette.success.main,
                  boxShadow: 1
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">Completed</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {statusCounts.completed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actions successfully completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  // Render actions table
  const renderActionsTable = (actionsList: StoreAction[]) => {
    if (isMobile) {
      // Mobile view - card-based layout instead of table
      return (
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          {actionsList.length === 0 ? (
            <Alert severity="info">
              No actions found
            </Alert>
          ) : (
            actionsList.map((action) => (
              <Card
                key={action.id}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderRadius: '8px',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Status and Action Type */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 1
                  }}>
                    <Chip
                      icon={getStatusIcon(action.actionstatus)}
                      label={action.actionstatus || 'Pending'}
                      size="small"
                      sx={{ 
                        backgroundColor: `${getStatusColor(action.actionstatus)}20`,
                        color: getStatusColor(action.actionstatus),
                        fontWeight: 'medium'
                      }}
                    />
                    <Chip
                      label={action.actionType}
                      size="small"
                      sx={{ 
                        backgroundColor: getActionTypeColor(action.actionType) + '20',
                        color: getActionTypeColor(action.actionType),
                        fontWeight: 'medium'
                      }}
                    />
                  </Box>
                  
                  {/* Customer Name */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    {action.name}
                  </Typography>
                  
                  {/* QR ID */}
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      QR ID:
                    </Typography>
                    <Typography variant="body2">
                      {action.Qrid}
                    </Typography>
                  </Box>
                  
                  {/* Who Collects */}
                  {action.who_collect && (
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        Collector:
                      </Typography>
                      <Typography variant="body2">
                        {action.who_collect}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Crew */}
                  {action.Crew && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        Crew:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            bgcolor: getStatusColor(action.actionstatus),
                            fontSize: '0.75rem'
                          }}
                        >
                          {action.Crew.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{action.Crew}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Created Time */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {formatDate(action.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {calculateElapsedTime(action.timestamp)}
                    </Typography>
                  </Box>
                  
                  {/* Progress Bar for In Progress actions */}
                  {action.actionstatus === 'In Progress' && action.inProgressStartTime && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {calculateElapsedTime(action.inProgressStartTime)}
                        </Typography>
                        <Typography variant="caption" color="info.main">
                          Est: 5 min
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(
                          100, 
                          ((Date.now() - action.inProgressStartTime.toMillis()) / (5 * 60 * 1000)) * 100
                        )} 
                        sx={{ 
                          height: 4, 
                          borderRadius: 2,
                          bgcolor: `${theme.palette.info.light}40`,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.info.main
                          }
                        }} 
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      );
    }
    
    // Desktop view - regular table
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="store actions table">
          <TableHead sx={{ backgroundColor: theme.palette.grey[50] }}>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Action Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>QR ID</TableCell>
              <TableCell>Who Collects</TableCell>
              <TableCell>Crew</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actionsList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No actions found
                </TableCell>
              </TableRow>
            ) :
              actionsList.map((action) => (
                <TableRow
                  key={action.id}
                  sx={{ 
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    '& > td': { py: 1.5 }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Chip
                        icon={getStatusIcon(action.actionstatus)}
                        label={action.actionstatus || 'Pending'}
                        size="small"
                        sx={{ 
                          backgroundColor: `${getStatusColor(action.actionstatus)}20`,
                          color: getStatusColor(action.actionstatus),
                          fontWeight: 'medium',
                          mb: action.actionstatus === 'In Progress' ? 1 : 0
                        }}
                      />
                      
                      {action.actionstatus === 'In Progress' && action.inProgressStartTime && (
                        <Box sx={{ width: '100%', mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {calculateElapsedTime(action.inProgressStartTime)}
                            </Typography>
                            <Typography variant="caption" color="info.main">
                              In progress
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(
                              100, 
                              ((Date.now() - action.inProgressStartTime.toMillis()) / (2 * 60 * 1000)) * 100
                            )} 
                            sx={{ 
                              height: 4, 
                              borderRadius: 2,
                              bgcolor: `${theme.palette.info.light}40`,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: theme.palette.info.main
                              }
                            }} 
                          />
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={action.actionType}
                      size="small"
                      sx={{ 
                        backgroundColor: getActionTypeColor(action.actionType) + '20',
                        color: getActionTypeColor(action.actionType),
                        fontWeight: 'medium'
                      }}
                    />
                  </TableCell>
                  <TableCell>{action.name}</TableCell>
                  <TableCell>{action.Qrid}</TableCell>
                  <TableCell>{action.who_collect || '—'}</TableCell>
                  <TableCell>
                    {action.Crew && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            bgcolor: getStatusColor(action.actionstatus),
                            fontSize: '0.75rem'
                          }}
                        >
                          {action.Crew.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{action.Crew}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">
                        {formatDate(action.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {calculateElapsedTime(action.timestamp)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Function to create an "Assist" ticket
  const createAssistTicket = async () => {
    try {
      await addDoc(collection(db, "StoreActions"), {
        actionType: "Assist",
        name: "Store Assistance", // Default name for Assist tickets
        Qrid: "ASSIST-" + new Date().getTime().toString().slice(-6), // Generate a unique ASSIST ID
        timestamp: serverTimestamp(),
        actionstatus: "Pending" // Set initial status to Pending
      });
      // Show success message
      alert("New 'Assist' ticket created successfully!");
      // Refresh the list
      setupRealTimeUpdates();
      // Trigger callback if provided
      if (onActionUpdated) {
        onActionUpdated();
      }
    } catch (error) {
      console.error("Error creating new ticket:", error);
      alert("Failed to create a new ticket.");
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
      <Paper 
        elevation={isMobile ? 0 : 1} 
        sx={{ 
          p: isMobile ? 1.5 : 3, 
          mb: 3, 
          borderRadius: 2,
          boxShadow: isMobile ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: isMobile ? 1 : 2
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            sx={{ 
              fontWeight: 'medium',
              mb: isMobile ? 2 : 0
            }}
          >
            Store Actions
          </Typography>
          
          <Box sx={{ display: 'flex', width: isMobile ? '100%' : 'auto' }}>
            {activeTab === 'today' ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={setupRealTimeUpdates}
                sx={{ mr: 1, flex: isMobile ? 1 : 'auto' }}
                size={isMobile ? "small" : "medium"}
              >
                Refresh
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={handleOpenHistorySearch}
                sx={{ mr: 1, flex: isMobile ? 1 : 'auto' }}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
            )}
            
            <Button
              variant={activeTab === 'history' ? 'contained' : 'outlined'}
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={() => setActiveTab(activeTab === 'today' ? 'history' : 'today')}
              sx={{ flex: isMobile ? 1 : 'auto' }}
              size={isMobile ? "small" : "medium"}
            >
              {activeTab === 'today' ? 'View History' : 'View Today'}
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{ mb: isMobile ? 1.5 : 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            value="today" 
            label={
              <Badge 
                badgeContent={actions.length} 
                color="primary"
                max={99}
                showZero
              >
                <Typography sx={{ mr: isMobile ? 0 : 1, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                  {isMobile ? "TODAY'S ACTIONS" : "Today's Actions"}
                </Typography>
              </Badge>
            } 
            id="tab-today"
          />
          <Tab 
            value="history" 
            label={
              <Badge 
                badgeContent={historyResults.length} 
                color="secondary"
                max={99}
                showZero
              >
                <Typography sx={{ mr: isMobile ? 0 : 1, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                  {isMobile ? "HISTORICAL" : "Historical Data"}
                </Typography>
              </Badge>
            } 
            id="tab-history"
          />
        </Tabs>
        
        {activeTab === 'today' && (
          <>
            {/* Status Count Cards */}
            {renderStatusCards()}
            
            {/* Today's Actions Table */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              sx={{ 
                mb: isMobile ? 1 : 2,
                mt: isMobile ? 0 : 1,
                fontWeight: isMobile ? 'medium' : 'regular'
              }}
            >
              Today's Actions
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : actions.length === 0 ? (
              <Alert severity="info">
                No store actions found for today.
              </Alert>
            ) : (
              renderActionsTable(actions)
            )}
          </>
        )}
        
        {activeTab === 'history' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                sx={{ 
                  mb: 2,
                  fontWeight: isMobile ? 'medium' : 'regular'
                }}
              >
                Historical Actions
              </Typography>
              
              {isMobile ? (
                <Card 
                  variant="outlined" 
                  sx={{ 
                    mb: 2,
                    p: 2,
                    borderRadius: '8px'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use the search button to find historical store actions.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleOpenHistorySearch}
                    fullWidth
                  >
                    Search Records
                  </Button>
                </Card>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Use the search button to find historical store actions. You can search by date, QR ID, or customer name.
                </Alert>
              )}
              
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : historyResults.length === 0 ? (
                <Alert severity="info">
                  No historical actions found. Please use the search function to find specific records.
                </Alert>
              ) : (
                <>
                  <Typography 
                    variant={isMobile ? "body2" : "subtitle1"} 
                    sx={{ 
                      mb: 2,
                      color: 'text.secondary'
                    }}
                  >
                    Showing {historyResults.length} results
                    {searchDate && ` for ${formatDisplayDate(searchDate)}`}
                    {searchQrid && ` with QR ID: ${searchQrid}`}
                    {searchName && ` with name: ${searchName}`}
                  </Typography>
                  {renderActionsTable(historyResults)}
                </>
              )}
            </Box>
          </>
        )}
      </Paper>
      
      {/* History Search Dialog */}
      <Dialog 
        open={historyOpen} 
        onClose={handleCloseHistorySearch}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontSize: isMobile ? '1.2rem' : '1.5rem'
        }}>
          Search Historical Actions
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              id="date"
              label="Date"
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              fullWidth
              id="qrid"
              label="QR ID"
              value={searchQrid}
              onChange={(e) => setSearchQrid(e.target.value)}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              fullWidth
              id="name"
              label="Customer Name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCloseHistorySearch} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSearchHistory} 
            variant="contained" 
            color="primary"
            disabled={!searchDate && !searchQrid && !searchName}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Floating Action Button */}
      <Zoom in={true} timeout={300} style={{ transitionDelay: '500ms' }}>
        <Fab
          color="secondary"
          variant="extended"
          aria-label="assist"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            px: 2
          }}
          onClick={createAssistTicket}
        >
          <HelpOutlineIcon sx={{ mr: 1 }} />
          Assist
        </Fab>
      </Zoom>
    </Box>
  );
};

export default StoreActions; 